import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Landmark,
  Cpu,
  MapPin,
  Download,
} from "lucide-react";
import ContactCTA from "./ContactCTA";

export default function SmruHero() {
  const trust = [
    {
      Icon: Landmark,
      line1: "UGC 2(f) Recognized",
      line2: "St. Mary's University",
    },
    {
      Icon: Cpu,
      line1: "AI.Prof Ecosystem",
      line2: "India's First AI B.Tech",
    },
    {
      Icon: MapPin,
      line1: "Near Ramoji Film City",
      line2: "Deshmukhi, Hyderabad",
    },
  ];

  return (
    <section
      id="top"
      className="relative pb-20 overflow-hidden pt-20 sm:pt-28 lg:pt-32"
    >
      {/* Soft brand blob — same treatment as the main website hero. */}
      <div className="absolute top-10 -right-32 w-[600px] h-[600px] rounded-full blur-3xl opacity-50 bg-gradient-to-tr from-aiorange to-aipeach float-fast" />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* LEFT — headline + CTAs */}
        <div className="lg:col-span-6 text-center lg:text-left">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-black tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[1.1]"
          >
            <span className="text-aiblue">India's First</span>
            <br />
            <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
              AI-Powered B.Tech
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-4 font-display font-black text-aiorange text-xl sm:text-3xl lg:text-[2.5rem] leading-tight"
          >
            at St. Mary's University (SMRU), Hyderabad
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-6 text-base sm:text-lg font-bold text-gray-600 max-w-xl mx-auto lg:mx-0"
          >
            A 4-year career ecosystem combining SMRU's academic legacy with
            AI.Prof's industry-powered training built for students going
            global.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-8 flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-3"
          >
            <ContactCTA className="cta-pulse items-center justify-center gap-2 bg-aiblue hover:bg-blue-700 text-white rounded-full px-7 py-3.5 font-semibold transition-colors" />
            <Link
              to="/RFC/brochure" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-gray-200 text-gray-800 rounded-full px-7 py-3.5 font-semibold hover:border-aiblue hover:text-aiblue transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Brochure
            </Link>
          </motion.div>
        </div>

        {/* RIGHT — AI.Prof × SMRU partnership lockup (no images). */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:col-span-6"
        >
          <div className="rounded-[2rem] bg-white shadow-2xl ring-1 ring-black/5 px-6 py-10 sm:px-12 sm:py-16 text-center">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-aiblue mb-8">
              In Partnership
            </span>
            <div className="flex items-center justify-center gap-4 sm:gap-8">
              <img
                src="/ai_prof.png"
                alt="AI.Prof"
                className="h-12 sm:h-20 w-auto object-contain"
              />
              <span className="text-aiorange font-black text-3xl sm:text-5xl leading-none">×</span>
              <img
                src="/partners/smru/smrulogo.png"
                alt="St. Mary's University (SMRU)"
                className="h-16 sm:h-24 w-auto object-contain"
              />
            </div>
            <p className="mt-8 text-sm sm:text-base font-bold text-gray-500 max-w-sm mx-auto">
              Together building India's first AI-powered B.Tech.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Trust strip — left-aligned, items kept close together. */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.6 }}
        className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10 mt-12 lg:mt-16 flex flex-col sm:flex-row sm:flex-wrap items-center sm:items-start gap-5 sm:gap-x-10 lg:gap-x-12"
      >
        {trust.map(({ Icon, line1, line2 }) => (
          <div key={line1} className="flex items-center gap-3">
            <span className="flex-shrink-0 h-10 w-10 rounded-full bg-aiblue/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-aiblue" />
            </span>
            <span className="text-sm leading-tight">
              <span className="block font-semibold text-gray-900">{line1}</span>
              <span className="block text-gray-500">{line2}</span>
            </span>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
